import { RowStatusMarkComponent } from '@modules/team-list-state/row-status-mark.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  BaseStitchListComponent,
  type StitchListItem,
} from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { Like } from '@modules/common/types/like';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { Contact } from '@modules/contacts/types/contact';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { SelectableItemDirective } from '@modules/drag-n-drop/directives/selectable.directive';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { ContactComponent } from '@modules/elements/components/contact/contact.component';
import { ElementsModule } from '@modules/elements/elements.module';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

/** A replica lags a write by well under a second, so three answers that began after the change and still list a cleared row mean the row was really re-flagged elsewhere. */
export const CLEARED_ROW_MAX_CONFIRMING_READS = 3;
/** The same, by the clock: 20 s is far beyond any replica lag and short enough that a missed signal never hides a row for long. */
export const CLEARED_ROW_MAX_HOLD_MS = 20_000;

/**
 * A row this tab cleared: when (the {@link ContactsListComponent.readClock} stamp and the wall clock), the offset of the
 * page that held it (unknown when it was not on screen) and how many later answers still listed it.
 */
interface ClearedRow {
  readonly clearedAt: number;
  readonly clearedAtMs: number;
  readonly heldAtOffset: number | undefined;
  stillListed: number;
}

@Component({
  selector: 'app-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: true,
  imports: [
    RowStatusMarkComponent,
    CommonModule,
    ListLoadErrorComponent,
    NoContentStubComponent,
    SelectableListDirective,
    LoadingIndicatorComponent,
    ListItemSeparatorComponent,
    ElementsModule,
    ClickDirective,
    SelectableItemDirective,
    ListSelectionBarComponent,
    Pagination2Component,
  ],
})
export class ContactsListComponent extends BaseStitchListComponent<Contact> implements OnInit, OnDestroy, OnChanges {
  /** Show per-row group-location badges (search results, where results span groups). */
  public readonly showLocationBadge = input<boolean>(false);
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  public items: StitchListItem<Contact>[] = [];

  @Input()
  public override filters: Like<ContactsFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickContact = new EventEmitter<Contact>();
  @Output()
  public readonly openContact = new EventEmitter<Contact>();
  @Output()
  public readonly doubleClickContact = new EventEmitter<Contact>();

  @ViewChildren(ContactComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  private readonly contactsService = inject(ContactsService);
  private readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });
  private readonly settledTenant = toSignal(
    this.contactsTenantScope?.selectedTeamId?.() ?? of(this.contactsTenantScope?.tenantId?.()),
    { initialValue: undefined },
  );
  private readonly contactsStateService = inject(ContactsAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  /** Rows this tab cleared from the list; see {@link dropRowsThatLeftThisList}. */
  private readonly clearedRows = new Map<string, ClearedRow>();
  /** Orders changes and reads against each other: bumped by every cleared-row change and every read that starts. */
  private readClock = 0;
  /** The offset of the read whose answer the rows on screen came from; the pagination moves {@link page} before that answer lands. */
  private shownFromOffset = 0;

  protected override getStitchType(): StitchType {
    return StitchType.CONTACT;
  }

  public override ngOnInit() {
    merge(
      this.contactsStateService.getRefreshAll(),
      this.linkedInfoService.getRefresh(),
      this.contactsService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();

    this.stitchEventsService
      .getEvents(() => this.stitchSignalSpaceIds(), StitchType.CONTACT, SignalStitchEventType.UPDATED)
      .pipe(takeUntil(this.alive))
      .subscribe(({ ids, changes }) => this.dropRowsThatLeftThisList(ids, changes));
  }

  /**
   * A Flagged, Snoozed or Follow-ups list is a filter on exactly the thing a row action just cleared, so the row
   * no longer belongs there the moment the change is made. The debounced reload that follows an `UPDATED`
   * signal confirms it, but it waits for its debounce and a read that can still return the old row, and a row
   * that sits in the list after "Unflag" reads as the action not having worked (#2396).
   *
   * The cleared ids stay excluded from every read that began BEFORE the signal ({@link withoutClearedRows}): a
   * read already in flight when the signal arrived may carry the old row and must not bring it back. Lists read
   * from a replica, so a read that began AFTER the signal can still return the old row too; the exclusion is
   * released only by an answer to a read of the page that held the row without the row, a Refresh, or the bounds
   * ({@link CLEARED_ROW_MAX_CONFIRMING_READS}, {@link CLEARED_ROW_MAX_HOLD_MS}). Any other page lacks the row
   * whether or not the change reached the replica, so its answer confirms nothing.
   */
  private dropRowsThatLeftThisList(ids: ReadonlyArray<string>, changes: Partial<Contact> | undefined): void {
    if (!changes || !ids.length) return;
    const touches = (key: 'flagged' | 'snoozed' | 'followed') => this.filters?.[key] === true && key in changes;
    const touched = (['flagged', 'snoozed', 'followed'] as const).filter(touches);
    if (!touched.length) return;
    if (touched.every((key) => !!changes[key])) {
      // Set again (Flag after Unflag) and no active filter is cleared by the same update: the row belongs here
      // again, so it is no longer held back. With several filters active a cleared one keeps it out (#2427).
      ids.forEach((id) => this.clearedRows.delete(id));
      return;
    }
    const clearedAt = ++this.readClock;
    const clearedAtMs = Date.now();
    const onScreen = new Set(this.items.map((row) => row.data.id));
    ids.forEach((id) =>
      this.clearedRows.set(id, {
        clearedAt,
        clearedAtMs,
        // A second signal for a row already taken off the screen keeps the page that held it.
        heldAtOffset: onScreen.has(id) ? this.shownFromOffset : this.clearedRows.get(id)?.heldAtOffset,
        stillListed: 0,
      }),
    );
    const leaving = new Set(ids);
    const kept = this.items.filter((row) => !leaving.has(row.data.id));
    if (kept.length === this.items.length) return;
    this.totalCount = Math.max(0, this.totalCount - (this.items.length - kept.length));
    this.items = kept;
    this.totalCountChange.emit(this.totalCount);
    // Clearing the last row of page 2+ lowers the last page below the one on screen, and a reload of a page
    // past the end is skipped: step back to the new last page and read it, instead of an empty page.
    if (this.page > this.getLastPage()) {
      this.page = this.getLastPage();
      this.displayedPage = this.page;
      this.loadPage(this.page);
    }
  }

  /**
   * Takes the rows this tab cleared out of an answer. An answer to a read that began before the change is stale
   * and is always filtered. An answer to a later read decides: a read of the page that held the row, without the
   * row, confirms the change and releases the exclusion; with the row it may be replica lag, so the row stays out
   * until the bounds are reached (the server wins then: the row was really listed again, e.g. flagged elsewhere
   * while this tab's socket was down). A page that never held the row (the clamp to page 1 after the last row of
   * page 2 left) lacks it either way and does not release it.
   */
  private withoutClearedRows(
    response: { items: Contact[]; count: number },
    startedAt: number,
    offset: number,
  ): { items: Contact[]; count: number } {
    const listed = new Set(response.items.map(({ id }) => id));
    const now = Date.now();
    for (const [id, row] of this.clearedRows) {
      if (row.clearedAt >= startedAt) continue;
      const heldTheRow = row.heldAtOffset === offset;
      const pastBounds =
        listed.has(id) &&
        (++row.stillListed >= CLEARED_ROW_MAX_CONFIRMING_READS || now - row.clearedAtMs >= CLEARED_ROW_MAX_HOLD_MS);
      if ((heldTheRow && !listed.has(id)) || pastBounds) this.clearedRows.delete(id);
    }
    if (!this.clearedRows.size) return response;
    const items = response.items.filter(({ id }) => !this.clearedRows.has(id));
    return { items, count: Math.max(0, response.count - (response.items.length - items.length)) };
  }

  public ngOnChanges(changes: SimpleChanges) {
    const ownerChanged =
      changes.tenantItems &&
      JSON.stringify(changes.tenantItems.previousValue?.map(({ accountId }) => accountId)) !==
        JSON.stringify(changes.tenantItems.currentValue?.map(({ accountId }) => accountId));
    if ('filters' in changes || ownerChanged) {
      this.clearedRows.clear();
      this.clearItems();
      this.resetItems();
    }
  }

  /** A reset re-reads from page 1 and cancels what was in flight, so no exclusion can outlive it. */
  public override resetItems(): void {
    this.clearedRows.clear();
    super.resetItems();
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Contact[]; count: number }> {
    const startedAt = ++this.readClock;
    return this.readItems(offset, limit).pipe(
      map((response) => {
        this.shownFromOffset = offset;
        return this.withoutClearedRows(response, startedAt, offset);
      }),
    );
  }

  private readItems(offset: number, limit: number): Observable<{ items: Contact[]; count: number }> {
    const requestContext = contactsOwnerContext(this.tenantItems(), this.settledTenant());
    if ((this.tenantItems() !== undefined || this.contactsTenantScope?.tenantId) && !requestContext) {
      return of({ items: [], count: 0 });
    }
    return this.contactsService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        requestContext,
      },
    );
  }

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const owners = this.tenantItems()
      ?.map(({ accountId }) => accountId)
      .filter(Boolean);
    if (owners) return owners;
    const tenant = this.contactsTenantScope?.effectiveTenantId?.();
    return tenant ? [tenant] : super.stitchSignalSpaceIds();
  }

  public handleDoubleClickItem(contact: Contact) {
    this.doubleClickContact.emit(contact);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Contact>): void {
    this.clickContact.emit(event.item.data);
  }
}
